import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Gift, Loader2, Pencil, Trash2, X, ImagePlus, Users, BadgeCheck, PhoneCall, CheckCheck,
} from "lucide-react";
import aapi, { formatApiError, inr, fmtDate } from "./aapi";
import { PageHeader, inputCls, selectCls, EmptyState } from "@/panel/components";
import { EASE } from "@/components/Motion";

const EMPTY_FORM = {
  title: "", badge: "", description: "",
  original_price: "", offer_price: "", image: "",
  target_user_ids: [], active: true,
};

const CLAIM_STYLES = {
  pending: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  contacted: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  closed: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
};

export default function AdminOffers() {
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [{ data: o }, { data: c }, { data: u }] = await Promise.all([
        aapi.get("/offers"),
        aapi.get("/offers/claims"),
        aapi.get("/users"),
      ]);
      setOffers(o);
      setClaims(c);
      setUsers(u);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Server-side user search — the default /users list is capped, so typing queries the API
  useEffect(() => {
    const t = setTimeout(() => {
      aapi.get("/users", { params: userSearch.trim() ? { search: userSearch.trim() } : {} })
        .then(({ data }) => setUsers(data))
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [userSearch]);

  const filteredUsers = useMemo(() => users, [users]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleTarget = (id) =>
    setForm((p) => ({
      ...p,
      target_user_ids: p.target_user_ids.includes(id)
        ? p.target_user_ids.filter((x) => x !== id)
        : [...p.target_user_ids, id],
    }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_200_000) {
      toast.error("Image 1.2MB se choti honi chahiye — compress karke daalein");
      return;
    }
    const r = new FileReader();
    r.onload = () => set("image", r.result);
    r.readAsDataURL(f);
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setForm({
      title: o.title, badge: o.badge || "", description: o.description || "",
      original_price: o.original_price ?? "", offer_price: o.offer_price ?? "",
      image: o.image || "", target_user_ids: o.target_user_ids || [], active: !!o.active,
    });
    setTab("offers");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.title.trim() || form.title.trim().length < 2) return toast.error("Title daalein");
    if (form.offer_price === "" || Number(form.offer_price) < 0) return toast.error("Offer price daalein");
    if (form.target_user_ids.length === 0) return toast.error("Kam se kam ek customer select karein");
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        badge: form.badge.trim(),
        description: form.description.trim(),
        original_price: form.original_price === "" ? null : Number(form.original_price),
        offer_price: Number(form.offer_price),
        image: form.image,
        target_user_ids: form.target_user_ids,
        active: form.active,
      };
      if (editingId) {
        await aapi.put(`/offers/${editingId}`, payload);
        toast.success("Offer update ho gaya");
      } else {
        await aapi.post("/offers", payload);
        toast.success(`Offer live — ${payload.target_user_ids.length} customer(s) ko dikhega`);
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeOffer = async (o) => {
    if (!window.confirm(`"${o.title}" delete karein? Iske claims bhi hat jayenge.`)) return;
    try {
      await aapi.delete(`/offers/${o.id}`);
      toast.success("Offer deleted");
      if (editingId === o.id) resetForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActive = async (o) => {
    try {
      await aapi.put(`/offers/${o.id}`, {
        title: o.title, badge: o.badge || "", description: o.description || "",
        original_price: o.original_price, offer_price: o.offer_price, image: o.image || "",
        target_user_ids: o.target_user_ids || [], active: !o.active,
      });
      toast.success(o.active ? "Offer paused" : "Offer activated");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setClaimStatus = async (c, status) => {
    try {
      await aapi.put(`/offers/claims/${c.id}`, { status });
      toast.success(`Claim marked ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const tabBtn = (id, label, tid) => (
    <button
      key={id}
      onClick={() => setTab(id)}
      data-testid={tid}
      className={`rounded-xl border px-5 py-2.5 text-xs font-semibold transition-all duration-300 ${
        tab === id
          ? "border-gold bg-gold/10 text-goldlight"
          : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="admin-offers-page">
      <PageHeader
        title="Targeted Offers"
        sub="Selected customers ke panel mein full-page luxury popup — product photo, price aur details fully aapke control mein. Customer claim kare to yahan request aati hai."
      />

      <div className="mb-6 flex gap-2">
        {tabBtn("offers", "Offers", "offers-tab")}
        {tabBtn("claims", `Claims${claims.filter((c) => c.status === "pending").length ? ` (${claims.filter((c) => c.status === "pending").length} new)` : ""}`, "claims-tab")}
      </div>

      {tab === "offers" && (
        <div className="grid gap-6 lg:grid-cols-2">
          {/* ------- form ------- */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="rounded-3xl border border-gold/20 bg-panel p-7"
            data-testid="offer-form"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
                  <Gift size={18} className="text-gold" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">
                    {editingId ? "EDIT OFFER" : "NEW OFFER"}
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">Popup ka har element yahan se control hota hai.</p>
                </div>
              </div>
              {editingId && (
                <button onClick={resetForm} data-testid="offer-cancel-edit" className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-neutral-500 hover:text-goldlight">
                  <X size={12} /> CANCEL
                </button>
              )}
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Product / Offer title</label>
                <input value={form.title} onChange={(e) => set("title", e.target.value)} maxLength={120}
                  placeholder="e.g. iPhone 18 Pro Max — Unlocked" className={`${inputCls} mt-2`} data-testid="offer-title-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Badge (optional)</label>
                  <input value={form.badge} onChange={(e) => set("badge", e.target.value)} maxLength={40}
                    placeholder="UNLOCKED" className={`${inputCls} mt-2`} data-testid="offer-badge-input" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Offer price (₹)</label>
                  <input type="number" min="0" value={form.offer_price} onChange={(e) => set("offer_price", e.target.value)}
                    placeholder="80000" className={`${inputCls} mt-2`} data-testid="offer-price-input" />
                </div>
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Original price (₹, optional — strikethrough dikhega)</label>
                <input type="number" min="0" value={form.original_price} onChange={(e) => set("original_price", e.target.value)}
                  placeholder="159900" className={`${inputCls} mt-2`} data-testid="offer-original-price-input" />
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Description (optional)</label>
                <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={600}
                  placeholder="Short pitch — condition, warranty, delivery etc." className={`${inputCls} mt-2 resize-y`} data-testid="offer-description-input" />
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Product photo</label>
                <div className="mt-2 flex items-center gap-3">
                  <label
                    className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-gold/40 bg-gold/[0.05] px-4 py-3 text-xs text-goldlight transition-colors hover:border-gold/70"
                    data-testid="offer-image-upload"
                  >
                    <ImagePlus size={15} /> Upload image
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                  </label>
                  <span className="font-mono text-[9px] text-neutral-600">ya URL paste karein · max 1.2MB</span>
                </div>
                <input value={form.image.startsWith("data:") ? "" : form.image}
                  onChange={(e) => set("image", e.target.value)}
                  placeholder="https://…" className={`${inputCls} mt-2 font-mono text-xs`} data-testid="offer-image-url-input" />
                {form.image && (
                  <div className="relative mt-3 h-36 overflow-hidden rounded-xl border border-white/10">
                    <img src={form.image} alt="preview" className="h-full w-full object-cover" data-testid="offer-image-preview" />
                    <button onClick={() => set("image", "")} data-testid="offer-image-remove"
                      className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-neutral-300 hover:text-rose-300">
                      <X size={13} />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  Target customers
                  <span className="text-gold/80" data-testid="offer-target-count">{form.target_user_ids.length} selected</span>
                </label>
                <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Name ya email se search…" className={`${inputCls} mt-2`} data-testid="offer-user-search" />
                <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.02] p-1" data-testid="offer-user-list">
                  {filteredUsers.slice(0, 60).map((u) => (
                    <label key={u.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-xs transition-colors hover:bg-white/[0.04] ${
                        form.target_user_ids.includes(u.id) ? "text-goldlight" : "text-neutral-400"
                      }`}
                      data-testid={`offer-target-${u.id}`}
                    >
                      <input type="checkbox" checked={form.target_user_ids.includes(u.id)} onChange={() => toggleTarget(u.id)}
                        className="h-3.5 w-3.5 accent-[rgb(var(--gold))]" />
                      <span className="truncate">{u.name}</span>
                      <span className="ml-auto truncate font-mono text-[10px] text-neutral-600">{u.email}</span>
                    </label>
                  ))}
                  {filteredUsers.length === 0 && (
                    <p className="px-3 py-4 text-center text-xs text-neutral-600">Koi user nahi mila</p>
                  )}
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 px-4 py-3" data-testid="offer-active-toggle">
                <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)}
                  className="h-4 w-4 accent-[rgb(var(--gold))]" />
                <span className="text-xs text-neutral-300">Active — selected customers ko turant popup dikhega (har login pe, jab tak active)</span>
              </label>

              <button onClick={save} disabled={busy} data-testid="offer-save-button"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
                {busy && <Loader2 size={15} className="animate-spin" />}
                {editingId ? "UPDATE OFFER" : `PUBLISH OFFER TO ${form.target_user_ids.length || "…"} CUSTOMER(S)`}
              </button>
            </div>
          </motion.div>

          {/* ------- offers list ------- */}
          <div className="space-y-4" data-testid="offers-list">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-gold" /></div>
            ) : offers.length === 0 ? (
              <EmptyState icon={Gift} title="Abhi koi offer nahi" sub="Pehla offer banayein — left side ka form use karein" testid="offers-empty" />
            ) : (
              offers.map((o) => (
                <motion.div key={o.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-panel"
                  data-testid={`offer-card-${o.id}`}>
                  <div className="flex gap-4 p-4">
                    {o.image ? (
                      <img src={o.image} alt={o.title} className="h-20 w-20 shrink-0 rounded-xl border border-white/10 object-cover" />
                    ) : (
                      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-gold/20 bg-gold/[0.06]">
                        <Gift size={20} className="text-gold/60" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-semibold text-neutral-100">{o.title}</p>
                        <span className={`rounded-full border px-2 py-0.5 font-mono text-[8px] tracking-[0.15em] ${
                          o.active ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-neutral-500/30 bg-white/5 text-neutral-500"
                        }`} data-testid={`offer-status-${o.id}`}>
                          {o.active ? "LIVE" : "PAUSED"}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-neutral-400">
                        {o.original_price ? <span className="mr-2 line-through opacity-60">{inr(o.original_price)}</span> : null}
                        <span className="font-semibold text-goldlight">{inr(o.offer_price)}</span>
                      </p>
                      <p className="mt-1.5 font-mono text-[9px] tracking-[0.1em] text-neutral-600">
                        {o.targets_count} TARGET(S) · {o.claims_count} CLAIM(S) · {fmtDate(o.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex border-t border-white/5">
                    <button onClick={() => startEdit(o)} data-testid={`offer-edit-${o.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 py-2.5 font-mono text-[10px] tracking-[0.15em] text-neutral-400 transition-colors hover:bg-white/[0.03] hover:text-goldlight">
                      <Pencil size={12} /> EDIT
                    </button>
                    <button onClick={() => toggleActive(o)} data-testid={`offer-toggle-${o.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 border-l border-white/5 py-2.5 font-mono text-[10px] tracking-[0.15em] text-neutral-400 transition-colors hover:bg-white/[0.03] hover:text-goldlight">
                      {o.active ? <X size={12} /> : <BadgeCheck size={12} />} {o.active ? "PAUSE" : "ACTIVATE"}
                    </button>
                    <button onClick={() => removeOffer(o)} data-testid={`offer-delete-${o.id}`}
                      className="flex flex-1 items-center justify-center gap-1.5 border-l border-white/5 py-2.5 font-mono text-[10px] tracking-[0.15em] text-neutral-400 transition-colors hover:bg-rose-400/10 hover:text-rose-300">
                      <Trash2 size={12} /> DELETE
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "claims" && (
        <div data-testid="claims-list">
          {claims.length === 0 ? (
            <EmptyState icon={PhoneCall} title="Koi claim nahi aaya abhi tak" sub="Jab koi customer offer claim karega, request yahan dikhegi" testid="claims-empty" />
          ) : (
            <div className="overflow-hidden rounded-2xl border border-white/10">
              {claims.map((c, i) => (
                <div key={c.id}
                  className={`flex flex-wrap items-center gap-4 px-5 py-4 ${i % 2 ? "bg-white/[0.015]" : "bg-panel"}`}
                  data-testid={`claim-row-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-neutral-100">
                      {c.user_name || "Customer"} <span className="ml-1 font-mono text-[10px] font-normal text-neutral-500">{c.user_email}</span>
                    </p>
                    <p className="mt-0.5 truncate text-xs text-neutral-400">
                      claimed <span className="text-goldlight">{c.offer_title}</span> · {fmtDate(c.created_at)}
                    </p>
                  </div>
                  <span className={`rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${CLAIM_STYLES[c.status] || CLAIM_STYLES.pending}`}
                    data-testid={`claim-status-${c.id}`}>
                    {c.status}
                  </span>
                  <select value={c.status} onChange={(e) => setClaimStatus(c, e.target.value)}
                    className={`${selectCls} !w-auto !py-2 text-xs`} data-testid={`claim-status-select-${c.id}`}>
                    <option value="pending">Pending</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              ))}
            </div>
          )}
          {claims.some((c) => c.status === "pending") && (
            <p className="mt-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.12em] text-amber-300/80">
              <CheckCheck size={13} /> Pending claims pe customer ko contact karke "Contacted"/"Closed" mark karein
            </p>
          )}
        </div>
      )}
    </div>
  );
}
